import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs/Subject';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';

import * as menuReducer from '../../../../ispm-admin/app/org-admin-settings/settings/store/org-admin-settings.reducer';
import { AskAIService } from '@ispm/dashboard/ask-ai/services/ask-a-i.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { IAskAIAnswer } from '@ispm/dashboard/ask-ai/interfaces/ask-a-i-answer.interface';
import { IAskAIQuestion} from '@ispm/dashboard/ask-ai/interfaces/ask-a-i-question.interface';
import { INavigationApplicationInterface, INavigationConfigurationInterface } from '@ispm/navigation/interfaces';
import { IState } from '../../../../ispm-admin/app/org-admin-settings/settings/store/org-admin-settings.reducer';
import { NavigationConfigService } from '@ispm/navigation/services/navigationConfig.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { getMenuState } from '../../../../ispm-admin/app/org-admin-settings/settings/store/org-admin-settings.selectors';

@Component({
  selector: 'gen-ask-ai',
  templateUrl: './ask-a-i.component.html',
  styleUrls: ['./ask-a-i.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AskAIComponent implements OnInit, OnDestroy {

  @ViewChild('scroll') public scrollContainer: ElementRef;

  public answers: Array<IAskAIAnswer> = [];
  public display = false;
  public enable = false;
  public loading = false;
  public question = '';
  public practitionerName = '';

  private chatHistory: any = {};
  private currentApplication: INavigationApplicationInterface|INavigationConfigurationInterface;
  private destroyer$: Subject<void> = new Subject<void>();

  constructor(
    public authService: AuthenticationService,
    private askAIService: AskAIService,
    private cdr: ChangeDetectorRef,
    private navigationConfigService: NavigationConfigService,
    private notificationMessageService: NotificationMessageService,
    private adminStore: Store<menuReducer.IState>,
  ) { }

  ngOnInit() {
    const name = this.authService.user?.info?.name;

    if (name && name[0].given[0].length > 0 && name[0].family[0].length > 0) {
      this.practitionerName = `${name[0].given[0][0]}${name[0].family[0][0]}`;
    }

    this.authService.user.isAdmin ? this.initAdmin() : this.init();

    this.authService.doLogout$.subscribe((res: boolean) => {
      if (res === true) {
        this.askAIService.deleteChatHistory()
      }
    })
  }

  public ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  init(): void {
    this.navigationConfigService.getCurrentApp()
      .pipe(
        switchMap((appConfiguration: INavigationApplicationInterface) => {
          return (appConfiguration.askAIEnable === true && appConfiguration.id !== this.currentApplication?.id)
            ? this.askAIService.appIsFunctionality(appConfiguration.id).pipe(
              map((response: boolean) => [appConfiguration, response])
            )
            : of([appConfiguration, false]);
        }),
        takeUntil(this.destroyer$.asObservable())
      )
      .subscribe(
        (response: [INavigationApplicationInterface, boolean]) => {
          this.currentApplication = response[0];
          this.enable = this.currentApplication.askAIEnable === true && response[1] === true;
          this.cdr.markForCheck();
        },
        () => {
          console.error('The connection to the AI engine failed');
        }
      );
  }

  initAdmin(): void {
    this.adminStore.select(getMenuState)
      .pipe(
        filter((state: IState) => !!state),
        filter((state: IState) => (state.applications && Object.keys(state.applications).length > 0)),
        switchMap((state: IState) => {
          let appConfiguration = state.configuration;

          return (appConfiguration.askAIEnable === true && appConfiguration.id !== this.currentApplication?.id)
            ? this.askAIService.appIsFunctionality(appConfiguration.id).pipe(
                map((response: boolean) => [appConfiguration, response])
              )
            : of([appConfiguration, false]);
        }),
      )
      .subscribe(
        (response: [INavigationConfigurationInterface, boolean]) => {
          this.currentApplication = response[0];
          this.enable = this.currentApplication.askAIEnable === true && response[1] === true;
          this.cdr.markForCheck();
        },
        (err: any) => {
          console.error('The connection to the AI engine failed', err);
        }
      );
  }

  askQuestion(): void {
    const question = this.question.trim();

    if (question === '' || this.enable === false) {
      return;
    }

    this.loading = true;

    this.answers.push({
      answer: 'Generating response <div class="question-loader"></div>',
      question: question
    } as IAskAIAnswer);

    this.scrollBottom();

    this.askAIService.getAnswer({
      question: question,
      appId: this.currentApplication.id
    } as IAskAIQuestion)
      .pipe(
        take(1)
      )
      .subscribe(
        (res: IAskAIAnswer) => {
          let preload = this.answers.slice(-1).pop();
          preload.answer = res.answer;
          this.question = '';

          this.chatHistory = this.askAIService.saveChatHistory(this.currentApplication.id, this.answers);

          this.loading = false;
          this.scrollBottom();
          this.cdr.markForCheck();
        },
        () => {
          this.notificationMessageService.error('The connection to the AI engine failed');
        }
      );
  }

  askQuestionKeyEvent(ask: KeyboardEvent) {
    if (ask.key === 'Enter' || ask.code === 'NumpadEnter') {
      this.askQuestion();
    }
  }

  clearHistory(): void {
    this.answers = [];
    this.chatHistory = this.askAIService.clearChatHistory(this.currentApplication.id);
  }

  getChatHistory(): void {
    this.answers = this.askAIService.getChatHistory(this.currentApplication.id);
  }

  closeSidebar(): void {
    this.answers = [];
    this.question = '';
    this.display = false;
    this.loading = false;
  }

  private scrollBottom(): void {
    const timer = setTimeout(() => {
      this.scrollToElement();
      clearTimeout(timer);
    }, 100);
  }

  private scrollToElement(): void {
    if (typeof this.scrollContainer.nativeElement?.scroll === 'function') {
      this.scrollContainer.nativeElement.scroll({
        top: this.scrollContainer.nativeElement.scrollHeight,
        left: 0,
        behavior: 'smooth'
      });
    }
  }
}
